
import type React from "react"

import { toast } from "sonner"
import { useState, useRef, useEffect } from "react"
import RoleDisplay from "./role-display"
import { useUpdateProfile } from "@/hooks"
import { StyledInput } from "@/components/ui/styled-input"
import { StyledLabel } from "@/components/ui/styled-label"
import { useTranslation } from 'react-i18next'
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/store/use-auth-store"
import { Loader2, Upload, Trash2 } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { fileService } from "@/services/file"
import { useServeFileById } from "@/hooks/file/use-file"
import { useQueryClient } from "@tanstack/react-query"
import type { User } from "@/types/user.types"

const ProfileForm = () => {
    const { t } = useTranslation();
    const { user, setUser } = useAuthStore()
    const queryClient = useQueryClient()
    const [firstName, setFirstName] = useState(user?.firstName || "")
    const [lastName, setLastName] = useState(user?.lastName || "")
    const [phone, setPhone] = useState(user?.phone || "")
    const [photoId, setPhotoId] = useState<string | null>(user?.photo || null)
    const [isUpdating, setIsUpdating] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const updateProfileMutation = useUpdateProfile()

    // Use the hook to get file content and convert to blob URL
    const { 
        data: imageUrl, 
        isLoading: isLoadingImage, 
        error: imageError 
    } = useServeFileById(photoId || '');

    useEffect(() => {
        if (user?.photo) {
            setPhotoId(user.photo);
        }
    }, [user]);

    const uploadImage = async (file: File) => {
        try {
            setIsUploading(true)
            const response = await fileService.uploadFile({ file, folder: 'profile-photos' });
            
            if (response.id) {
                const updateResponse = await updateProfileMutation.mutateAsync({
                    userId: user?.id || '',
                    firstName,
                    lastName,
                    phone,
                    photo: response.id
                });

                setPhotoId(response.id);
                setUser(updateResponse as User);
                // Invalidate queries to refresh profile data in dashboard header
                queryClient.invalidateQueries({ queryKey: ['userProfile'] });
                queryClient.invalidateQueries({ queryKey: ['file', response.id] });
                toast.success(t('profile.profilePictureUploadedSuccessfully'));
            } else {
                toast.error(t('profile.failedToUploadProfilePicture'));
            }
        } catch (error) {
            console.error("Upload error", error);
            toast.error(t('profile.failedToUploadProfilePicture'));
        } finally {
            setIsUploading(false);
        }
    }

    const deleteImage = async (fileId: string) => {
        try {
            setIsDeleting(true);
            const response = await fileService.deleteFile(fileId);
            
            if (response.success) {
                const updateResponse = await updateProfileMutation.mutateAsync({
                    userId: user?.id || '',
                    firstName,
                    lastName,
                    phone,
                    photo: null
                });

                setPhotoId(null);
                setUser(updateResponse as User);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }

                // Invalidate queries to refresh profile data in dashboard header
                queryClient.invalidateQueries({ queryKey: ['userProfile'] });
                queryClient.invalidateQueries({ queryKey: ['file', fileId] });
            } else {
                toast.error(t('profile.failedToRemoveProfilePicture'));
            }
        } catch (error) {
            console.error("Delete error", error);
            toast.error(t('profile.failedToRemoveProfilePicture'));
        } finally {
            setIsDeleting(false);
        }
    }

    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            await uploadImage(selectedFile);
        }
    }

    const handleImageUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const handleImageDeleteClick = () => {
        if (photoId) {
            deleteImage(photoId);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            setIsUpdating(true)

            const updatedUser = await updateProfileMutation.mutateAsync({
                userId: user?.id || '',
                firstName,
                lastName,
                phone,
                photo: photoId ? photoId : undefined
            })

            if (!updatedUser) {
                throw new Error("Failed to update profile")
            }

            setUser(updatedUser as User);

            // Invalidate queries to refresh profile data in dashboard header
            queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            toast.success(t('profile.profileUpdatedSuccessfully'))
        } catch (error) {
            console.error("Update error", error)
            toast.error(t('profile.failedToUpdateProfile'))
        } finally {
            setIsUpdating(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col items-center sm:flex-row sm:items-start gap-6 mb-8">
                <div className="flex flex-col items-center gap-4">
                    <Avatar className="w-32 h-32">
                        <AvatarImage 
                            src={imageUrl || undefined} 
                            alt={`${firstName} ${lastName}`} 
                            className="object-cover" 
                        />
                        <AvatarFallback className="text-2xl">
                            {firstName && lastName ? `${firstName[0]}${lastName[0]}` : user?.email?.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>

                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleImageUploadClick}
                            disabled={isUploading || isDeleting}
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
{t('profile.uploading')}
                                </>
                            ) : (
                                <>
                                    <Upload className="mr-2 h-4 w-4" />
{t('profile.upload')}
                                </>
                            )}
                        </Button>

                        {photoId && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleImageDeleteClick}
                                disabled={isUploading || isDeleting}
                                className="text-destructive border-destructive hover:bg-destructive/10"
                            >
                                {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                            </Button>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageChange}
                        disabled={isUploading || isDeleting}
                    />
                </div>

                <div className="w-full space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <StyledLabel htmlFor="firstName">{t('profile.firstName')}</StyledLabel>
                            <StyledInput
                                id="firstName"
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder={t('profile.enterFirstName')}
                            />
                        </div>

                        <div>
                            <StyledLabel htmlFor="lastName">{t('profile.lastName')}</StyledLabel>
                            <StyledInput
                                id="lastName"
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder={t('profile.enterLastName')}
                            />
                        </div>
                    </div>

                    <div>
                        <StyledLabel htmlFor="email">{t('profile.email')}</StyledLabel>
                        <StyledInput 
                            id="email" 
                            type="email"
                            placeholder={t('profile.email')}
                            value={user?.email || ""} 
                            disabled 
                        />
                        <p className="text-sm text-gray-500 mt-1">{t('profile.emailCannotBeChanged')}</p>
                    </div>

                    <div>
                        <StyledLabel htmlFor="phone">{t('profile.phone')}</StyledLabel>
                        <StyledInput
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder={t('profile.enterPhone')}
                        />
                    </div>
                </div>
            </div>
            <RoleDisplay />
            <div className="flex justify-end">
                <Button type="submit" className="main-dark-button" disabled={isUpdating || isUploading || isDeleting}>
                    {isUpdating ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('profile.updating')}
                        </>
                    ) : (
                        t('profile.saveChanges')
                    )}
                </Button>
            </div>
        </form>
    )
}

export default ProfileForm